import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

class ProductInventory extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateInventory);
    
    // Initially check if variant is selected after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.#checkVariantSelection();
    }, 100);
  }
  
  /**
   * Checks if a variant is selected and hides inventory if not
   */
  #checkVariantSelection() {
    const productForm = this.closest('product-form-component');
    const variantIdInput = productForm?.querySelector('input[name="id"][ref="variantId"]');
    const hasVariantSelected = variantIdInput && variantIdInput.value && variantIdInput.value !== '';
    
    // Only hide if we're sure no variant is selected AND the element doesn't already have display set
    if (!hasVariantSelected) {
      const currentDisplay = window.getComputedStyle(this).display;
      // Only hide if it's not already hidden by the liquid template (for threshold reasons)
      if (currentDisplay !== 'none') {
        this.style.display = 'none';
      }
    }
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateInventory);
  }

  /**
   * Updates the inventory.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateInventory = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Check if a variant is actually selected - use event data first, then fallback to input
    const variantResource = event.detail.resource;
    const hasVariantFromEvent = variantResource && variantResource.id;
    
    // Also check the input as fallback
    const productForm = this.closest('product-form-component');
    const variantIdInput = productForm?.querySelector('input[name="id"][ref="variantId"]');
    const hasVariantFromInput = variantIdInput && variantIdInput.value && variantIdInput.value !== '';
    
    const hasVariantSelected = hasVariantFromEvent || hasVariantFromInput;

    // Hide inventory if no variant is selected
    if (!hasVariantSelected) {
      this.style.display = 'none';
      return;
    }

    const newInventory = event.detail.data.html?.querySelector('product-inventory');

    // If new inventory block exists, morph it and update display
    if (newInventory) {
      morph(this, newInventory, { childrenOnly: true });
      // Copy display style from new element (handles show/hide based on threshold)
      const newDisplay = newInventory.style.display || window.getComputedStyle(newInventory).display;
      // Show inventory if variant is selected and new inventory should be displayed
      if (hasVariantSelected && newDisplay !== 'none') {
        this.style.display = '';
      } else {
        this.style.display = 'none';
      }
    } else {
      // If no new inventory block (inventory above threshold), hide the current one
      this.style.display = 'none';
    }
  };
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
